// src/pages/Actions.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { actionsAPI } from '../services/api';
import toast from 'react-hot-toast';
import {
  Search, RefreshCw, Filter, X, Activity,
  User, Building2, Briefcase, Phone, Eye, Edit2, ChevronLeft, ChevronRight,
} from 'lucide-react';

const CATEGORIE_META = {
  'Candidat':  { color: '#3B82F6', bg: 'rgba(59,130,246,0.12)',  icon: User },
  'Ressource': { color: '#8B5CF6', bg: 'rgba(139,92,246,0.10)',  icon: Briefcase },
  'Contact':   { color: '#10B981', bg: 'rgba(16,185,129,0.10)',  icon: Phone },
  'Société':   { color: '#F59E0B', bg: 'rgba(245,158,11,0.10)',  icon: Building2 },
  'Besoin':    { color: '#FF4100', bg: 'rgba(255,65,0,0.10)',     icon: Activity },
};

const PAGE_SIZE = 20;
const lbl = { fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 4, display: 'block' };
const inp = { width: '100%', height: 36, padding: '0 10px', border: '1px solid var(--border)', borderRadius: 6, background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.875rem' };

function CategorieBadge({ categorie }) {
  const meta = CATEGORIE_META[categorie] || { color: '#6B7280', bg: 'rgba(107,114,128,0.10)', icon: Activity };
  const Icon = meta.icon;
  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'2px 8px', borderRadius:6, fontSize:'0.72rem', fontWeight:600, color:meta.color, background:meta.bg }}>
      <Icon size={11}/>{categorie || 'Autre'}
    </span>
  );
}

function ActionModal({ action, onClose, onSave }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    type_action: action.type_action || '',
    action:      action.action || '',
    responsable_manager: action.responsable_manager || '',
  });

  const handleSave = async () => {
    try {
      await actionsAPI.patch(action.id, form);
      toast.success('Action mise à jour');
      onSave();
      onClose();
    } catch { toast.error('Erreur mise à jour'); }
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleString('fr-FR') : '—';
  const entityName = action.candidate_name || action.resource_name || action.company_name || action.contact_name || action.reference_entite || '—';

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background:'var(--surface)', borderRadius:12, width:'min(640px,95vw)', maxHeight:'85vh', overflow:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.4)' }}>
        {/* Header */}
        <div style={{ padding:'20px 24px 16px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <CategorieBadge categorie={action.categorie}/>
            <span style={{ fontWeight:700, fontSize:'1rem' }}>{action.type_action || 'Action'}</span>
          </div>
          <div style={{ display:'flex', gap:8 }}>
            {!editing && (
              <button className="btn btn-secondary btn-sm" onClick={() => setEditing(true)}>
                <Edit2 size={13}/> Modifier
              </button>
            )}
            <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}><X size={16}/></button>
          </div>
        </div>

        <div style={{ padding:'20px 24px', display:'flex', flexDirection:'column', gap:16 }}>
          {/* Entité liée */}
          <div className="card" style={{ padding:'12px 16px', display:'flex', gap:16, flexWrap:'wrap' }}>
            <div><span style={lbl}>Entité</span><span style={{ fontWeight:600 }}>{entityName}</span></div>
            <div><span style={lbl}>Responsable</span><span>{action.responsable_manager || '—'}</span></div>
            <div><span style={lbl}>Date début</span><span>{fmtDate(action.date_debut)}</span></div>
            <div><span style={lbl}>Créé le</span><span>{fmtDate(action.boond_created_at || action.created_at)}</span></div>
            <div><span style={lbl}>Mis à jour</span><span>{fmtDate(action.boond_updated_at || action.updated_at)}</span></div>
          </div>

          {editing ? (
            <>
              <div>
                <label style={lbl}>Type d'action</label>
                <input style={inp} value={form.type_action} onChange={e => setForm(f => ({...f, type_action: e.target.value}))}/>
              </div>
              <div>
                <label style={lbl}>Description</label>
                <textarea
                  style={{ ...inp, height:120, resize:'vertical', padding:'8px 10px' }}
                  value={form.action}
                  onChange={e => setForm(f => ({...f, action: e.target.value}))}
                />
              </div>
              <div>
                <label style={lbl}>Responsable</label>
                <input style={inp} value={form.responsable_manager} onChange={e => setForm(f => ({...f, responsable_manager: e.target.value}))}/>
              </div>
              <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
                <button className="btn btn-ghost" onClick={() => setEditing(false)}>Annuler</button>
                <button className="btn btn-primary" onClick={handleSave}>Enregistrer</button>
              </div>
            </>
          ) : (
            <div>
              <label style={lbl}>Description</label>
              <div style={{ background:'var(--bg-elevated)', padding:'12px 14px', borderRadius:8, border:'1px solid var(--border)', fontSize:'0.875rem', lineHeight:1.6, whiteSpace:'pre-wrap', color:'var(--text-primary)' }}>
                {action.action || '—'}
              </div>
            </div>
          )}

          {action.email_entite && (
            <div><span style={lbl}>Email</span><a href={`mailto:${action.email_entite}`} style={{ color:'#3B82F6' }}>{action.email_entite}</a></div>
          )}
          {action.phone_entite && (
            <div><span style={lbl}>Téléphone</span><span>{action.phone_entite}</span></div>
          )}
        </div>
      </div>
    </div>
  );
}


export default function Actions() {
  const [items,       setItems]       = useState([]);
  const [total,       setTotal]       = useState(0);
  const [loading,     setLoading]     = useState(true);
  const [search,      setSearch]      = useState('');
  const [categorie,   setCategorie]   = useState('');
  const [responsable, setResponsable] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [page,        setPage]        = useState(1);
  const [selected,    setSelected]    = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await actionsAPI.list({
        categorie:   categorie   || undefined,
        responsable: responsable || undefined,
        search:      search      || undefined,
        skip:  (page - 1) * PAGE_SIZE,
        limit: PAGE_SIZE,
      });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch { toast.error('Erreur chargement actions'); }
    finally { setLoading(false); }
  }, [categorie, responsable, search, page]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totalPages  = Math.ceil(total / PAGE_SIZE);
  const hasFilters  = !!(categorie || responsable);

  const entityName = (a) =>
    a.candidate_name || a.resource_name || a.company_name || a.contact_name || a.reference_entite || '—';

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';

  return (
    <div>
      {selected && (
        <ActionModal
          action={selected}
          onClose={() => setSelected(null)}
          onSave={fetchData}
        />
      )}

      <div className="page-header">
        <div className="page-header-left">
          <h1>Actions</h1>
          <p>Historique de toutes les actions Boond Manager</p>
        </div>
        <div className="page-header-actions">
          <button className="btn btn-secondary" onClick={() => setShowFilters(s => !s)}>
            <Filter size={15}/> Filtres
            {hasFilters && <span style={{ marginLeft:4, background:'#FF4100', color:'white', borderRadius:'50%', width:16, height:16, display:'inline-flex', alignItems:'center', justifyContent:'center', fontSize:'0.6rem' }}>1</span>}
          </button>
          <button className="btn btn-secondary" onClick={fetchData}><RefreshCw size={15}/></button>
        </div>
      </div>

      {/* Recherche */}
      <div style={{ display:'flex', gap:10, marginBottom:16 }}>
        <div style={{ position:'relative', flex:1 }}>
          <Search size={14} style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:'var(--text-muted)' }}/>
          <input
            style={{ paddingLeft:34, height:38, border:'1px solid var(--border)', borderRadius:8, fontSize:'0.875rem', outline:'none', width:'100%', boxSizing:'border-box', background:'var(--bg-elevated)', color:'var(--text-primary)' }}
            placeholder="Action, type, responsable, entité…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && fetchData()}
          />
        </div>
        {(search || hasFilters) && (
          <button className="btn btn-ghost btn-sm" onClick={() => { setSearch(''); setCategorie(''); setResponsable(''); setPage(1); }}><X size={14}/> Réinitialiser</button>
        )}
      </div>

      {showFilters && (
        <div className="card" style={{ marginBottom:16, padding:'14px 16px' }}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(200px,1fr))', gap:10 }}>
            <div>
              <label style={lbl}>Catégorie</label>
              <select style={{ ...inp }} value={categorie} onChange={e => { setCategorie(e.target.value); setPage(1); }}>
                <option value="">Toutes</option>
                {Object.keys(CATEGORIE_META).map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>Responsable</label>
              <input style={inp} placeholder="Filtrer par responsable…" value={responsable}
                onChange={e => { setResponsable(e.target.value); setPage(1); }}/>
            </div>
          </div>
        </div>
      )}

      <div style={{ fontSize:'0.8125rem', color:'var(--text-muted)', marginBottom:12 }}>
        {loading ? 'Chargement…' : `${total} action${total!==1?'s':''} ${totalPages>1?`— page ${page}/${totalPages}`:''}`}
      </div>

      {loading ? (
        <div style={{ display:'flex', justifyContent:'center', padding:'48px', gap:10, color:'var(--text-muted)' }}>
          <div className="spinner"/>Chargement…
        </div>
      ) : items.length === 0 ? (
        <div className="card" style={{ textAlign:'center', padding:'48px 20px', color:'var(--text-muted)' }}>
          <Activity size={36} style={{ opacity:.25, marginBottom:12 }}/>
          <p style={{ margin:0 }}>Aucune action trouvée</p>
        </div>
      ) : (
        <div className="card" style={{ overflow:'hidden' }}>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead>
                <tr style={{ background:'var(--bg-elevated)', borderBottom:'1px solid var(--border)' }}>
                  {['Date début','Catégorie','Type','Entité','Action','Responsable','Créé le','Mis à jour',''].map(h => (
                    <th key={h} style={{ padding:'10px 14px', textAlign:'left', fontSize:'0.72rem', fontWeight:700, color:'var(--text-muted)', whiteSpace:'nowrap', textTransform:'uppercase', letterSpacing:'0.04em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((a, i) => (
                  <tr key={a.id}
                    style={{ borderBottom:'1px solid var(--border)', cursor:'pointer', transition:'background .15s' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-elevated)'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}
                    onClick={() => setSelected(a)}
                  >
                    <td style={{ padding:'10px 14px', whiteSpace:'nowrap', fontSize:'0.8rem', color:'var(--text-muted)' }}>{fmtDate(a.date_debut)}</td>
                    <td style={{ padding:'10px 14px' }}><CategorieBadge categorie={a.categorie}/></td>
                    <td style={{ padding:'10px 14px', fontSize:'0.82rem', whiteSpace:'nowrap' }}>{a.type_action || '—'}</td>
                    <td style={{ padding:'10px 14px', fontSize:'0.82rem', fontWeight:600, whiteSpace:'nowrap', maxWidth:140, overflow:'hidden', textOverflow:'ellipsis' }}>{entityName(a)}</td>
                    <td style={{ padding:'10px 14px', maxWidth:260, fontSize:'0.82rem', color:'var(--text-secondary)' }}>
                      <span style={{ display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden' }}>{a.action || '—'}</span>
                    </td>
                    <td style={{ padding:'10px 14px', fontSize:'0.8rem', whiteSpace:'nowrap' }}>{a.responsable_manager || '—'}</td>
                    <td style={{ padding:'10px 14px', fontSize:'0.75rem', color:'var(--text-muted)', whiteSpace:'nowrap' }}>{fmtDate(a.boond_created_at || a.created_at)}</td>
                    <td style={{ padding:'10px 14px', fontSize:'0.75rem', color:'var(--text-muted)', whiteSpace:'nowrap' }}>{fmtDate(a.boond_updated_at || a.updated_at)}</td>
                    <td style={{ padding:'10px 14px' }}>
                      <Eye size={14} color="var(--text-muted)"/>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:8, marginTop:20 }}>
          <button className="btn btn-secondary btn-sm" disabled={page===1} onClick={() => setPage(p=>p-1)}><ChevronLeft size={14}/></button>
          <span style={{ fontSize:'0.85rem', color:'var(--text-muted)', minWidth:80, textAlign:'center' }}>Page {page} / {totalPages}</span>
          <button className="btn btn-secondary btn-sm" disabled={page===totalPages} onClick={() => setPage(p=>p+1)}><ChevronRight size={14}/></button>
        </div>
      )}
    </div>
  );
}
